import { config } from 'uniswap/src/config'
import { uniswapUrls } from 'uniswap/src/constants/urls'
import { createApiClient } from 'uniswap/src/data/apiClients/createApiClient'
import { SwappableTokensParams } from 'uniswap/src/data/apiClients/tradingApi/useTradingApiSwappableTokensQuery'
import {
  ApprovalRequest,
  ApprovalResponse,
  BridgeQuote,
  ChainId,
  CheckApprovalLPRequest,
  CheckApprovalLPResponse,
  ClaimLPFeesRequest,
  ClaimLPFeesResponse,
  ClassicQuote,
  CreateLPPositionRequest,
  CreateLPPositionResponse,
  CreateSwapRequest,
  CreateSwapResponse as BaseCreateSwapResponse,
  DecreaseLPPositionRequest,
  DecreaseLPPositionResponse,
  DutchQuoteV2,
  GetOrdersResponse,
  GetSwappableTokensResponse,
  GetSwapsResponse,
  IncreaseLPPositionRequest,
  IncreaseLPPositionResponse,
  IndicativeQuoteRequest,
  IndicativeQuoteResponse,
  MigrateLPPositionRequest,
  MigrateLPPositionResponse,
  OrderRequest,
  OrderResponse,
  PriorityQuote,
  QuoteRequest,
  QuoteResponse,
  Routing,
  TransactionHash,
  UniversalRouterVersion,
} from 'uniswap/src/data/tradingApi/__generated__'

// TradingAPI team is looking into updating type generation to produce the following types for it's current QuoteResponse type:
// See: https://linear.app/uniswap/issue/API-236/explore-changing-the-quote-schema-to-pull-out-a-basequoteresponse
export type DiscriminatedQuoteResponse =
  | ClassicQuoteResponse
  | DutchQuoteResponse
  | PriorityQuoteResponse
  | BridgeQuoteResponse

export type DutchQuoteResponse = QuoteResponse & {
  quote: DutchQuoteV2
  routing: Routing.DUTCH_V2
}

export type PriorityQuoteResponse = QuoteResponse & {
  quote: PriorityQuote
  routing: Routing.PRIORITY
}

export type ClassicQuoteResponse = QuoteResponse & {
  quote: ClassicQuote
  routing: Routing.CLASSIC
}

export type BridgeQuoteResponse = QuoteResponse & {
  quote: BridgeQuote
  routing: Routing.BRIDGE
}

export const TRADING_API_CACHE_KEY = 'TradingApi'

// Check if we have a valid trading API configuration
// For custom deployments, we allow the API to work without a key
const hasValidTradingApiConfig = uniswapUrls.tradingApiUrl

const TradingApiClient = hasValidTradingApiConfig ? createApiClient({
  baseUrl: uniswapUrls.tradingApiUrl,
  additionalHeaders: config.tradingApiKey && config.tradingApiKey !== 'key' ? {
    'x-api-key': config.tradingApiKey,
  } : {},
}) : null

export type WithV4Flag<T> = T & { v4Enabled: boolean }

// 🔧 Extended type to support new API response structure with calls array and gasLimit
export type CreateSwapResponse = BaseCreateSwapResponse & {
  calls?: Array<{
    data: string
    to: string
    value: string
  }>
  chainId?: number
  from?: string
  gasLimit?: string  // 新增：API 现在返回 gasLimit
}

export async function fetchQuote({
  v4Enabled,
  ...params
}: WithV4Flag<QuoteRequest>): Promise<DiscriminatedQuoteResponse> {
  if (!TradingApiClient) {
    throw new Error('Trading API is not configured for this environment')
  }

  try {
    // 🔧 Log the exact request being sent to /quote/temp
 
    
    const rawResult = await TradingApiClient.post<any>(uniswapUrls.tradingApiPaths.quote, {
      body: JSON.stringify(params),
      headers: {
        'x-universal-router-version': v4Enabled ? UniversalRouterVersion._2_0 : UniversalRouterVersion._1_2,
      },
    })
    
    
    // 🔧 ADAPTER: Transform custom API response to expected format
    // Check if the response is already in the expected QuoteResponse format
    if (rawResult && typeof rawResult === 'object' && 'quote' in rawResult && 'routing' in rawResult) {
      return rawResult as DiscriminatedQuoteResponse
    }
    
    // Handle the new quote/temp response format with detailed route information
    if (rawResult && typeof rawResult === 'object' && 'quoteId' in rawResult) {

      
      // 🔧 Extract permitData from the raw response if it exists
      const permitData = rawResult.permitData || null
      
      const adaptedResult: ClassicQuoteResponse = {
        requestId: rawResult.quoteId || 'unknown',
        quote: rawResult as ClassicQuote,
        routing: Routing.CLASSIC,
        permitData: permitData,
      }
      return adaptedResult
    }
    
    return rawResult as DiscriminatedQuoteResponse
  } catch (error) {

    throw error
  }
}

export async function fetchIndicativeQuote(params: IndicativeQuoteRequest): Promise<IndicativeQuoteResponse> {
  // 🚫 Indicative Quote API is temporarily disabled
  throw new Error('Indicative Quote API is temporarily disabled')
  
  // if (!TradingApiClient) {
  //   throw new Error('Trading API is not configured for this environment')
  // }
  // return await TradingApiClient.post<IndicativeQuoteResponse>(uniswapUrls.tradingApiPaths.indicativeQuote, {
  //   body: JSON.stringify(params),
  // })
}

export async function fetchSwap({ v4Enabled, ...params }: any): Promise<CreateSwapResponse> {
  if (!TradingApiClient) {
    throw new Error('Trading API is not configured for this environment')
  }
  
  // 🔧 转换 CreateSwapRequest 格式为直接 API 请求格式
  const quote = params.quote
  const transformedParams: any = {}
  
  
  // 从 quote 对象中提取基本参数
  if (quote) {
    // 从路由中提取 tokenIn 和 tokenOut，但对原生代币使用正确的地址格式
    if (quote.route && quote.route[0] && quote.route[0].length > 0) {
      const firstStep = quote.route[0][0]
      const lastStep = quote.route[0][quote.route[0].length - 1]
      
      // H2 链的包装代币地址（WH2）
      const H2_WRAPPED_ADDRESS = '0x1E69ED5584b562Da61c69433b6bB5430f351B1de'.toLowerCase()
      const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'
      
      // 提取 tokenIn (第一个步骤的输入 token)
      if (firstStep.tokenIn) {
        let tokenInAddress = firstStep.tokenIn.address
        
        // 🔧 关键修复：如果原始请求的 currency 是原生代币，但 quote 返回的是包装代币
        // 自动替换成零地址（后端在路由计算时会自动转换，但 swap 需要使用原生代币地址）
        const currencyInIsNative = (params as any)._currencyInIsNative
        if (currencyInIsNative && 
            firstStep.tokenIn.chainId === 2582 && 
            tokenInAddress.toLowerCase() === H2_WRAPPED_ADDRESS) {
          console.log('🔧 检测到原始请求是原生 H2，但 quote 返回 WH2，替换为零地址:', { 
            from: tokenInAddress, 
            to: ZERO_ADDRESS 
          })
          tokenInAddress = ZERO_ADDRESS
        }
        
        transformedParams.tokenInAddress = tokenInAddress
        transformedParams.tokenInChainId = firstStep.tokenIn.chainId
      }
      
      // 提取 tokenOut (最后一个步骤的输出 token)
      if (lastStep.tokenOut) {
        let tokenOutAddress = lastStep.tokenOut.address
        
        // 🔧 关键修复：如果原始请求的 currency 是原生代币，但 quote 返回的是包装代币
        // 自动替换成零地址（后端在路由计算时会自动转换，但 swap 需要使用原生代币地址）
        const currencyOutIsNative = (params as any)._currencyOutIsNative
        if (currencyOutIsNative && 
            lastStep.tokenOut.chainId === 2582 && 
            tokenOutAddress.toLowerCase() === H2_WRAPPED_ADDRESS) {
          console.log('🔧 检测到原始请求是原生 H2，但 quote 返回 WH2，替换为零地址:', { 
            from: tokenOutAddress, 
            to: ZERO_ADDRESS 
          })
          tokenOutAddress = ZERO_ADDRESS
        }
        
        transformedParams.tokenOutAddress = tokenOutAddress
        transformedParams.tokenOutChainId = lastStep.tokenOut.chainId
      }

    }
    
    // 提取金额
    if (quote.amount) {
      transformedParams.amount = quote.amount
    }
    
    // 设置交易类型 (默认为 exactIn，因为我们看到的是输入金额)
    transformedParams.type = 'exactIn'
    
    // 添加 recipient (swapper) 和 simulateFromAddress
    if (quote.swapper) {
      transformedParams.recipient = quote.swapper
      transformedParams.simulateFromAddress = quote.swapper
    }
    
    // 添加协议
    transformedParams.protocols = ['v2', 'v3']
  }
  
  // 添加 permit 参数（如果存在）
  if (params.permit) {
    transformedParams.permit = params.permit
  } 
  
  // 添加 signature 和 permitData（如果存在）
  if (params.signature) {
    transformedParams.signature = params.signature
    transformedParams.permit = {
      "permitSignature": params.signature,
      "permitNonce": params.permitData.values.details.nonce,
      "permitExpiration": params.permitData.values.details.expiration,
      "permitAmount": params.permitData.values.details.amount,
      "permitSigDeadline": params.permitData.values.sigDeadline,
    }

  } 
  
  if (params.permitData) {
    transformedParams.permitData = params.permitData

  } 
  
  
  try {
    const response = await TradingApiClient.post<CreateSwapResponse>(uniswapUrls.tradingApiPaths.swap, {
      body: JSON.stringify(transformedParams),
      headers: {
        'x-universal-router-version': v4Enabled ? '2.0' : '1.2',
      },
    })
    

    
    return response
  } catch (error: unknown) {
    const errorObj = error as any
    
    // 如果是 500 错误，添加更多上下文信息
    if (errorObj.message?.includes('500')) {
      console.error('🚨 500 Internal Server Error context:', {
        tradingApiUrl: uniswapUrls.tradingApiUrl,
        swapPath: uniswapUrls.tradingApiPaths.swap,
        fullUrl: `${uniswapUrls.tradingApiUrl}${uniswapUrls.tradingApiPaths.swap}`,
        requestParams: params,
        v4Enabled,
        timestamp: new Date().toISOString(),
      })
    }
    
    throw error
  }
}

export async function fetchCheckApproval(params: ApprovalRequest): Promise<ApprovalResponse> {
  if (!TradingApiClient) {
    throw new Error('Trading API is not configured for this environment')
  }
  return await TradingApiClient.post<ApprovalResponse>(uniswapUrls.tradingApiPaths.approval, {
    body: JSON.stringify(params),
  })
}

export interface TopTokensGraphqlRequest {
  variables: {
    skip: number
    first: number
    searchQuery?: string  // 用于文本搜索（symbol或name）
    id?: string           // 用于地址搜索
  }
  query: string
  operationName: string
}

export interface TopTokensGraphqlResponse<TToken = any> {
  data?: {
    tokens?: TToken[]
  }
  errors?: Array<{
    message?: string
  } | null>
}

export async function fetchGraphqlTopTokens(
  body: TopTokensGraphqlRequest,
): Promise<TopTokensGraphqlResponse> {
  if (!TradingApiClient) {
    throw new Error('Trading API is not configured for this environment')
  }

  const response = await TradingApiClient.post<TopTokensGraphqlResponse>('/v1/graphql/topTokens', {
    body: JSON.stringify(body),
  })

  if (response?.errors && response.errors.length > 0) {
    const message =
      response.errors
        .map((error) => error?.message)
        .filter((msg): msg is string => Boolean(msg))
        .join(', ') || 'Top tokens request failed'
    throw new Error(message)
  }

  return response
}

// 定义 Pools API 的请求和响应类型
export interface PoolsRequest {
  variables: {
    token0: string
    token1: string
    feeTier?: string  // 添加可选的 feeTier 参数
  }
  query: string
  operationName: string
}

export interface PoolsResponse {
  pools: Array<{
    chainId: number
    sqrtPriceX96: string
    fee: number
    token0: string
    poolId: string
    token1: string
    liquidity: string
    totalLiquidityUsd: string
    protocolVersion: string
    tick: number
  }>
}

export async function fetchPools(params: PoolsRequest): Promise<PoolsResponse> {
  if (!TradingApiClient) {
    throw new Error('Trading API is not configured for this environment')
  }
  

  
  try {
    const response = await TradingApiClient.post<PoolsResponse>(uniswapUrls.tradingApiPaths.pools, {
      body: JSON.stringify(params),
    })
    

    
    return response
  } catch (error: unknown) {
    console.error('🚨 fetchPools 错误:', error)
    throw error
  }
}

export async function submitOrder(params: OrderRequest): Promise<OrderResponse> {
  if (!TradingApiClient) {
    throw new Error('Trading API is not configured for this environment')
  }
  return await TradingApiClient.post<OrderResponse>(uniswapUrls.tradingApiPaths.order, {
    body: JSON.stringify(params),
  })
}

export async function fetchOrders({ orderIds }: { orderIds: string[] }): Promise<GetOrdersResponse> {
  if (!TradingApiClient) {
    throw new Error('Trading API is not configured for this environment')
  }
  return await TradingApiClient.get<GetOrdersResponse>(uniswapUrls.tradingApiPaths.orders, {
    params: {
      orderIds: orderIds.join(','),
    },
  })
}

export async function fetchSwappableTokens(params: SwappableTokensParams): Promise<GetSwappableTokensResponse> {
  // 🚫 Disabled: swappableTokens API removed
  throw new Error('SwappableTokens API has been disabled')
}

export async function createLpPosition(params: CreateLPPositionRequest): Promise<CreateLPPositionResponse> {
  if (!TradingApiClient) {
    throw new Error('Trading API is not configured for this environment')
  }
  return await TradingApiClient.post<CreateLPPositionResponse>(uniswapUrls.tradingApiPaths.createLp, {
    body: JSON.stringify({
      ...params,
    }),
  })
}
export async function decreaseLpPosition(params: DecreaseLPPositionRequest): Promise<DecreaseLPPositionResponse> {
  if (!TradingApiClient) {
    throw new Error('Trading API is not configured for this environment')
  }
  return await TradingApiClient.post<DecreaseLPPositionResponse>(uniswapUrls.tradingApiPaths.decreaseLp, {
    body: JSON.stringify({
      ...params,
    }),
  })
}
export async function increaseLpPosition(params: IncreaseLPPositionRequest): Promise<IncreaseLPPositionResponse> {
  if (!TradingApiClient) {
    throw new Error('Trading API is not configured for this environment')
  }
  return await TradingApiClient.post<IncreaseLPPositionResponse>(uniswapUrls.tradingApiPaths.increaseLp, {
    body: JSON.stringify({
      ...params,
    }),
  })
}
export async function checkLpApproval(
  params: CheckApprovalLPRequest,
  headers?: Record<string, string>,
): Promise<CheckApprovalLPResponse> {
  if (!TradingApiClient) {
    throw new Error('Trading API is not configured for this environment')
  }
  return await TradingApiClient.post<CheckApprovalLPResponse>(uniswapUrls.tradingApiPaths.lpApproval, {
    body: JSON.stringify({
      ...params,
    }),
    headers,
  })
}

// ListPositions API types
export interface ListPositionsRequest {
  address?: string
  chainIds?: number[]
  positionStatuses?: string[]
  protocolVersions?: string[]
  pageSize?: number
  pageToken?: string
}

export interface ListPositionsResponse {
  positions?: any[]
  pageToken?: string
}

export async function fetchPositions(
  params: ListPositionsRequest,
  headers?: Record<string, string>,
): Promise<ListPositionsResponse> {
  if (!TradingApiClient) {
    throw new Error('Trading API is not configured for this environment')
  }
  return await TradingApiClient.post<ListPositionsResponse>(uniswapUrls.tradingApiPaths.positions, {
    body: JSON.stringify({
      ...params,
    }),
    headers,
  })
}

export async function claimLpFees(params: ClaimLPFeesRequest): Promise<ClaimLPFeesResponse> {
  if (!TradingApiClient) {
    throw new Error('Trading API is not configured for this environment')
  }
  return await TradingApiClient.post<ClaimLPFeesResponse>(uniswapUrls.tradingApiPaths.claimLpFees, {
    body: JSON.stringify({
      ...params,
    }),
  })
}

export async function fetchSwaps(params: { txHashes: TransactionHash[]; chainId: ChainId }): Promise<GetSwapsResponse> {
  if (!TradingApiClient) {
    throw new Error('Trading API is not configured for this environment')
  }
  return await TradingApiClient.get<GetSwapsResponse>(uniswapUrls.tradingApiPaths.swaps, {
    params: {
      txHashes: params.txHashes.join(','),
      chainId: params.chainId,
    },
  })
}

export async function migrateLpPosition(params: MigrateLPPositionRequest): Promise<MigrateLPPositionResponse> {
  if (!TradingApiClient) {
    throw new Error('Trading API is not configured for this environment')
  }
  return await TradingApiClient.post<MigrateLPPositionResponse>(uniswapUrls.tradingApiPaths.migrate, {
    body: JSON.stringify({
      ...params,
    }),
  })
}
